// ============================================================
// FILE: src/pages/ProductDetail.jsx  →  SINGLE PRODUCT PAGE
// What a visitor sees after clicking a product: big photo,
// specifications table, MOQ, packaging, export markets and
// the "Request a Quotation" form.
// ============================================================

import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Package, Globe2, FileText, Boxes } from "lucide-react";
import PageHero from "../components/PageHero";
import InquiryForm from "../components/InquiryForm";
import { Reveal } from "../components/Reveal";
import { useSEO } from "../hooks/useSEO";
import { api } from "../lib/api";

export default function ProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useSEO(
    product ? `${product.name} — Evolve Trade & Logistics` : "Product — Evolve Trade & Logistics",
    product ? product.desc.slice(0, 155) : "Export-ready products with verified quality and documentation."
  );

  useEffect(() => {
    setProduct(null);
    setNotFound(false);
    api.product(slug)
      .then((p) => {
        setProduct(p);
        api.products(p.category).then((list) => setRelated(list.filter((x) => x.slug !== slug).slice(0, 4))).catch(() => {});
      })
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="container-x flex min-h-[70vh] flex-col items-center justify-center pt-24 text-center" data-testid="product-not-found">
        <h1 className="font-display text-4xl font-bold">Product not found</h1>
        <p className="mt-3 text-muted-foreground">The product you are looking for does not exist or has moved.</p>
        <Link to="/products" className="btn-primary mt-8" data-testid="product-not-found-back">Back to Catalogue</Link>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="container-x pt-40">
        <div className="grid gap-10 lg:grid-cols-2">
          <div className="aspect-[4/3] animate-pulse rounded-2xl bg-slate-900/5 dark:bg-white/5" />
          <div className="space-y-4">
            <div className="h-8 w-2/3 animate-pulse rounded bg-slate-900/5 dark:bg-white/5" />
            <div className="h-24 w-full animate-pulse rounded bg-slate-900/5 dark:bg-white/5" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <PageHero
        label={product.category}
        title={product.name}
        description={product.desc}
        crumbs={[{ label: "Products", to: "/products" }, { label: product.name }]}
      />

      <section className="pb-20" data-testid="product-detail">
        <div className="container-x grid gap-12 lg:grid-cols-2">
          <Reveal>
            <div className="group relative">
              <div className="pointer-events-none absolute -inset-6 rounded-[2.5rem] bg-teal-500/10 blur-3xl" />
              <div className="relative overflow-hidden rounded-[2rem] border border-slate-900/10 dark:border-white/10" data-testid="product-image-frame">
                <img src={product.image} alt={product.name} className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/40 to-transparent" />
                <span className="glass absolute left-5 top-5 rounded-full px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-teal-300">{product.hs}</span>
              </div>
            </div>

            <div className="mt-8 grid grid-cols-2 gap-4">
              <div className="glass rounded-2xl p-5" data-testid="product-moq">
                <Package className="h-5 w-5 text-teal-500" />
                <p className="mt-3 text-xs font-semibold uppercase tracking-widest text-muted-foreground">Minimum Order</p>
                <p className="mt-1 font-display text-sm font-semibold">{product.moq}</p>
              </div>
              <div className="glass rounded-2xl p-5" data-testid="product-packaging">
                <Boxes className="h-5 w-5 text-teal-500" />
                <p className="mt-3 text-xs font-semibold uppercase tracking-widest text-muted-foreground">Packaging</p>
                <p className="mt-1 font-display text-sm font-semibold">{product.packaging}</p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="card-surface !p-0" data-testid="product-specs-card">
              <div className="border-b border-slate-900/10 px-7 py-5 dark:border-white/10">
                <h2 className="flex items-center gap-2.5 font-display text-xl font-semibold">
                  <FileText className="h-5 w-5 text-teal-500" /> Specifications
                </h2>
              </div>
              <dl>
                {Object.entries(product.specs).map(([k, v], i) => (
                  <div key={k} className={`flex items-center justify-between px-7 py-4 ${i % 2 === 0 ? "bg-slate-900/[0.03] dark:bg-white/[0.03]" : ""}`} data-testid={`spec-${k.toLowerCase().replace(/[\s./]+/g, "-")}`}>
                    <dt className="text-sm text-muted-foreground">{k}</dt>
                    <dd className="text-sm font-semibold">{v}</dd>
                  </div>
                ))}
              </dl>
            </div>

            <div className="mt-6 card-surface" data-testid="product-markets">
              <h3 className="flex items-center gap-2.5 font-display text-lg font-semibold">
                <Globe2 className="h-5 w-5 text-teal-500" /> Key Export Markets
              </h3>
              <div className="mt-4 flex flex-wrap gap-2">
                {product.countries.map((c) => <span key={c} className="chip">{c}</span>)}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="pb-24" data-testid="product-inquiry-section">
        <div className="container-x grid gap-12 lg:grid-cols-[1fr_1.3fr]">
          <Reveal>
            <p className="section-label">Request a Quotation</p>
            <h2 className="mt-4 font-display text-3xl font-semibold leading-tight tracking-tight sm:text-4xl">
              Get pricing for <span className="text-gradient">{product.name}</span>
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
              Share your volume and destination — our trade desk responds within 24 hours with product pricing, packaging options and freight estimates.
            </p>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="card-surface">
              <InquiryForm defaultProduct={product.name} type="product" testIdPrefix="product-inquiry" />
            </div>
          </Reveal>
        </div>
      </section>

      {related.length > 0 && (
        <section className="pb-24 sm:pb-32" data-testid="related-products">
          <div className="container-x">
            <div className="mb-10 flex items-center justify-between">
              <h2 className="font-display text-2xl font-semibold tracking-tight sm:text-3xl">More in {product.category}</h2>
              <Link to="/products" className="btn-secondary !px-5 !py-2 !text-xs" data-testid="related-view-all">
                <ArrowLeft className="h-3.5 w-3.5" /> Full Catalogue
              </Link>
            </div>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {related.map((p) => (
                <Link key={p.slug} to={`/products/${p.slug}`} className="group block" data-testid={`related-card-${p.slug}`}>
                  <div className="relative overflow-hidden rounded-2xl border border-slate-900/10 dark:border-white/10">
                    <div className="aspect-[4/3] overflow-hidden">
                      <img src={p.image} alt={p.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/85 to-transparent" />
                    <div className="absolute inset-x-0 bottom-0 flex items-center justify-between p-4">
                      <h3 className="font-display text-sm font-semibold text-white">{p.name}</h3>
                      <ArrowRight className="h-4 w-4 text-teal-400 opacity-0 transition-opacity group-hover:opacity-100" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}
